import { moveToFulfilled, update } from '@ms/yammer-data-support';
import { MessageDeletionOutcome, ThreadMessageLevel } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/loadCuratedThreadListActions.types';
import type { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { FeedThreadCountsOnServerIncrementAction } from '../../domains/feedRealtime/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadAction,
  NestedThreadDeepLinkTopLevelLoadAction,
  NestedThreadLoadAction,
  NestedThreadSecondLevelRepliesReceivedAction,
  SecondLevelMessage,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  PublishReplyMessageFulfilledAction,
  PublishersBeginReplyToDirectMessageAction,
  PublishersBeginReplyToGroupMessageAction,
  PublishersBeginReplyToNetworkQuestionMessageAction,
  PublishersBeginReplyToTeamsMeetingMessageAction,
  PublishersBeginReplyToUserStorylineMessageAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import type { Message } from '../messages/types';

import type { SecondLevelReplies, SecondLevelReplyPages } from './types';

const initialState: SecondLevelReplies = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

export type SecondLevelRepliesAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | FeedThreadCountsOnServerIncrementAction
  | NestedThreadLoadAction
  | NestedThreadDeepLinkTopLevelLoadAction
  | NestedThreadDeepLinkSecondLevelLoadAction
  | NestedThreadListLoadFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | PublishersBeginReplyToDirectMessageAction
  | PublishersBeginReplyToGroupMessageAction
  | PublishersBeginReplyToUserStorylineMessageAction
  | PublishersBeginReplyToNetworkQuestionMessageAction
  | PublishersBeginReplyToTeamsMeetingMessageAction
  | PublishReplyMessageFulfilledAction
  | NestedThreadSecondLevelRepliesReceivedAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | DeleteMessageFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | CuratedThreadListLoadAction;

const stateWithPreviousPage = (
  state: SecondLevelReplies,
  payload: SecondLevelRepliesLoadPreviousFulfilledAction['payload']
): SecondLevelReplies => {
  const {
    entities: { secondLevelReplies },
    topLevelMessageId,
  } = payload;

  const {
    hasPreviousPage,
    totalPreviousCount,
    totalUnseenPreviousCount,
    edges: previousEdges,
    startCursor,
  } = secondLevelReplies[topLevelMessageId];
  const { edges: existingEdges, newMessagesFromCurrentUser } = state.fulfilled.byId[topLevelMessageId];

  if (existingEdges.length === 0) {
    return stateWithInitialPage(state, payload);
  }

  const messagesFromCurrentUserWithoutPagedMessages = newMessagesFromCurrentUser.filter(
    (messageId) => !previousEdges.find((edge) => edge.nodeId === messageId)
  );

  const updatedValues = {
    startCursor,
    hasPreviousPage,
    totalPreviousCount,
    totalUnseenPreviousCount,
    edges: [...previousEdges, ...existingEdges],
    showPublisher: true,
    newMessagesFromCurrentUser: messagesFromCurrentUserWithoutPagedMessages,
    realtimeMessageIds: new Set<Message['id']>(),
  };

  return update(state, topLevelMessageId, updatedValues);
};

const stateWithNextPage = (
  state: SecondLevelReplies,
  payload: SecondLevelRepliesLoadNextFulfilledAction['payload']
): SecondLevelReplies => {
  const {
    entities: { secondLevelReplies },
    topLevelMessageId,
  } = payload;

  const {
    hasNextPage,
    totalNextCount,
    totalUnseenNextCount,
    edges: nextEdges,
    endCursor,
  } = secondLevelReplies[topLevelMessageId];
  const { edges: existingEdges, newMessagesFromCurrentUser } = state.fulfilled.byId[topLevelMessageId];
  const messagesFromCurrentUserWithoutPagedMessages = newMessagesFromCurrentUser.filter(
    (messageId) => !nextEdges.find((edge) => edge.nodeId === messageId)
  );

  const updatedValues = {
    endCursor,
    hasNextPage,
    totalNextCount,
    totalUnseenNextCount,
    edges: [...existingEdges, ...nextEdges],
    newMessagesFromCurrentUser: messagesFromCurrentUserWithoutPagedMessages,
    realtimeMessageIds: new Set<Message['id']>(),
  };

  return update(state, topLevelMessageId, updatedValues);
};

const stateWithNewReply = (state: SecondLevelReplies, payload: PublishReplyMessageFulfilledAction['payload']) => {
  const { isSecondLevelReply, messageId, parentMessageId } = payload;

  if (!isSecondLevelReply) {
    return moveToFulfilled(state, {
      [messageId]: {
        id: messageId,
        startCursor: null,
        hasPreviousPage: false,
        totalPreviousCount: 0,
        totalUnseenPreviousCount: 0,
        endCursor: null,
        hasNextPage: false,
        totalNextCount: 0,
        totalUnseenNextCount: 0,
        edges: [],
        newMessagesFromCurrentUser: [],
        realtimeMessageIds: new Set<Message['id']>(),
      },
    });
  }

  const secondLevelReplies = state.fulfilled.byId[parentMessageId!];

  if (secondLevelReplies == null) {
    return state;
  }

  const { newMessagesFromCurrentUser } = secondLevelReplies;

  return update(state, parentMessageId!, {
    newMessagesFromCurrentUser: [...newMessagesFromCurrentUser, messageId],
  });
};

type InitialPagePayload =
  | SecondLevelRepliesLoadLastFulfilledAction['payload']
  | SecondLevelRepliesLoadPreviousFulfilledAction['payload'];
const stateWithInitialPage = (state: SecondLevelReplies, { entities, topLevelMessageId }: InitialPagePayload) => {
  const stateWithPage = moveToFulfilled(state, entities.secondLevelReplies);

  return update(stateWithPage, topLevelMessageId, { showPublisher: true });
};

const stateWithNewRepliesCount = (
  state: SecondLevelReplies,
  payload: FeedThreadCountsOnServerIncrementAction['payload']
) => {
  if (payload.threadLevel !== 2 || !payload.parentId) {
    return state;
  }

  return stateWithRealtimeReplies(state, [{ messageId: payload.messageId, topLevelMessageId: payload.parentId }]);
};

const stateWithRealtimeReplies = (state: SecondLevelReplies, realtimeMessages: SecondLevelMessage[]) =>
  realtimeMessages.reduce((updatedState: SecondLevelReplies, newSecondLevelMessage: SecondLevelMessage) => {
    const { messageId, topLevelMessageId } = newSecondLevelMessage;
    const secondLevelMessages = state.fulfilled.byId[topLevelMessageId];

    if (secondLevelMessages == null) {
      return state;
    }

    const { newMessagesFromCurrentUser, realtimeMessageIds, edges } = secondLevelMessages;

    const isFromCurrentUser = newMessagesFromCurrentUser.includes(messageId);
    const isPaginatedEdge = edges.findIndex((edge) => edge.nodeId === messageId) > -1;

    if (isFromCurrentUser || isPaginatedEdge) {
      return state;
    }

    const updatedRealtimeMessageIds = new Set(realtimeMessageIds.add(messageId));

    return update(updatedState, topLevelMessageId, { realtimeMessageIds: updatedRealtimeMessageIds });
  }, state);

const stateWithNewThreadsTracked = (
  state: SecondLevelReplies,
  secondLevelReplies: Record<Message['id'], SecondLevelReplyPages> | undefined
) => moveToFulfilled(state, { ...secondLevelReplies, ...state.fulfilled.byId });

const stateWithSecondLevelPublisherOpen = (
  state: SecondLevelReplies,
  payload:
    | PublishersBeginReplyToGroupMessageAction['payload']
    | PublishersBeginReplyToUserStorylineMessageAction['payload']
    | PublishersBeginReplyToDirectMessageAction['payload']
    | PublishersBeginReplyToNetworkQuestionMessageAction['payload']
    | PublishersBeginReplyToTeamsMeetingMessageAction['payload']
) => {
  const topLevelReplyId =
    payload.threadLevel === ThreadMessageLevel.TOP_LEVEL_REPLY ? payload.messageId : payload.parentMessageId;

  if (topLevelReplyId == null) {
    return state;
  }

  return update(state, topLevelReplyId, { showPublisher: true });
};

const stateWithSecondLevelMessageForTopLevelMessageRemoved = (
  state: SecondLevelReplies,
  topLevelMessageId: Message['id'],
  secondLevelMessageId: Message['id']
) => {
  const secondLevelRepliesPage = state.fulfilled.byId[topLevelMessageId];
  if (!secondLevelRepliesPage) {
    return state;
  }

  const edgesWithoutMessage = secondLevelRepliesPage.edges.filter((edge) => edge.nodeId !== secondLevelMessageId);
  const newMessagesFromCurrentUserWithoutMessage = secondLevelRepliesPage.newMessagesFromCurrentUser.filter(
    (messageId) => messageId !== secondLevelMessageId
  );

  return update(state, topLevelMessageId, {
    edges: edgesWithoutMessage,
    newMessagesFromCurrentUser: newMessagesFromCurrentUserWithoutMessage,
  });
};

const deleteMessage = (
  state: SecondLevelReplies,
  { messageId, deletionOutcome, threadLevel, parentMessageId }: DeleteMessageFulfilledAction['payload']
) => {
  const wasSecondLevelReplyRemoved =
    deletionOutcome === MessageDeletionOutcome.MESSAGE_REMOVED && threadLevel === ThreadMessageLevel.SECOND_LEVEL_REPLY;

  if (wasSecondLevelReplyRemoved && parentMessageId !== undefined) {
    return stateWithSecondLevelMessageForTopLevelMessageRemoved(state, parentMessageId, messageId);
  }

  return state;
};

const reducer: Reducer<SecondLevelReplies, SecondLevelRepliesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithNewThreadsTracked(state, action.payload.entities.secondLevelReplies);
    case 'FEED_THREAD_COUNTS_ON_SERVER.INCREMENT':
      return stateWithNewRepliesCount(state, action.payload);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithPreviousPage(state, action.payload);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithNextPage(state, action.payload);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'PUBLISHER.BEGIN_REPLY_TO_DIRECT_MESSAGE':
      return stateWithSecondLevelPublisherOpen(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_USER_STORYLINE_MESSAGE':
      return stateWithSecondLevelPublisherOpen(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_GROUP_MESSAGE':
      return stateWithSecondLevelPublisherOpen(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_NETWORK_QUESTION_MESSAGE':
      return stateWithSecondLevelPublisherOpen(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_TEAMS_MEETING_MESSAGE':
      return stateWithSecondLevelPublisherOpen(state, action.payload);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return stateWithNewReply(state, action.payload);
    case 'NESTED_THREAD.SECOND_LEVEL_REPLIES_RECEIVED':
      return stateWithRealtimeReplies(state, action.payload.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return stateWithInitialPage(state, action.payload);
    case 'MESSAGE.DELETE_FULFILLED':
      return deleteMessage(state, action.payload);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.secondLevelReplies);
    default:
      return state;
  }
};

export default reducer;
